// DATAflor: 3D-Gaussian-Splats (XGRIDS LCC/LCC2) im Potree-Viewer.
//
// Das LCC-Web-SDK (libs/lcc/lcc-sdk-three.umd.js) hängt sich als window.LCC ein und nimmt sich
// three aus window.THREE (das Potree beim Laden bereitstellt, siehe three-setup.js). Es rendert
// seine Splats als Mesh-Gruppe in einer normalen three-Szene; wir geben ihm viewer.scene.scene,
// die Potree in jedem Render-Pass nach der Punktwolke mit renderer.render() zeichnet. Weil das
// SDK Tiefe schreibt (setWriteDepth) und Potrees EDL-/HQ-Pass seine Punktwolke per Tiefentest
// darüberkomponiert, verdecken sich Punkte und Splats gegenseitig korrekt.
//
// Das SDK muss in jedem Frame vor dem Rendern angestoßen werden (LCCRender.update, Sortierung
// und Nachladen) – das übernimmt viewer.render() über LCCLayer.update().

import * as THREE from "../../../libs/three.js/build/three.module.js";
import {EventDispatcher} from "../../EventDispatcher.js";

// LCC/LCC2-Daten aus dem Lixel-Workflow sind z-up (gemessen am K2-Datensatz: z-Ausdehnung 33 m
// bei 235 x 184 m Grundfläche), genau wie Potree. Die modelMatrix aus dem XGRIDS-three-Beispiel
// ((x, y, z) -> (-x, z, y)) kippt z-up-Daten nur in three's y-up-Welt und ist hier fehl am Platz.
// Datensätze mit anderer Achslage lassen sich über options.transform ausrichten.
export const LCC_TO_POTREE = new THREE.Matrix4();

export class LCCEntry {

	constructor(name, dataPath, modelMatrix) {
		this.name = name;
		this.dataPath = dataPath;
		this.modelMatrix = modelMatrix;
		this.lcc = null;        // SDK-Objekt (LccObject): setAlpha, setVisible, setClipBox, raycast ...
		this.mesh = null;       // THREE.Group des SDK, liegt in viewer.scene.scene
		this.loaded = false;
	}

	get visible() {
		return this.mesh ? this.mesh.visible : true;
	}

	set visible(value) {
		if (this.lcc) {
			this.lcc.setVisible(value);
		}
	}

	/** Umgrenzung in Potree-Weltkoordinaten (SDK liefert sie im LCC-Datensystem) */
	getBoundingBox() {
		if (!this.lcc) {
			return null;
		}
		const b = this.lcc.getBounds();
		if (!b) {
			return null;
		}
		const box = new THREE.Box3(
			new THREE.Vector3(b.min.x, b.min.y, b.min.z),
			new THREE.Vector3(b.max.x, b.max.y, b.max.z));

		return box.applyMatrix4(this.modelMatrix);
	}
}

export class LCCLayer extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.entries = [];
		this.lastCamera = null;
		this.writeDepth = true;
		this.pickTargets = null;
	}

	/** true, wenn das LCC-Web-SDK vor potree.js bzw. danach als Script geladen wurde */
	static get available() {
		return typeof window !== 'undefined' && !!(window.LCC && window.LCC.LCCRender);
	}

	get sdk() {
		return LCCLayer.available ? window.LCC.LCCRender : null;
	}

	/**
	 * Lädt einen LCC/LCC2-Datensatz in die Szene.
	 *
	 * @param {string} dataPath   URL der meta.lcc (LCC) bzw. der .lcc2-Datei (LCC2)
	 * @param {object} options
	 *   name          Anzeigename
	 *   transform     THREE.Matrix4 vom LCC-Datensystem in Potree-Welt; ersetzt position/rotationZ/scale
	 *   position      THREE.Vector3 | [x, y, z]: Lage des LCC-Ursprungs in Potree-Welt (z.B. UTM)
	 *   rotationZ     Drehung um die Hochachse in Grad
	 *   scale         Maßstab (Standard 1)
	 *   appKey        Schlüssel von XGRIDS (ohne Key zeichnet das SDK ein Wasserzeichen)
	 *   useEnv        Umgebungs-Splats mitladen (Standard true)
	 *   useIndexDB    lokaler Daten-Cache im Browser (Standard true)
	 *   useLoadingEffect  Lade-Animation (Standard false)
	 *   onProgress    (anteil 0..1) => void
	 * @returns {Promise<LCCEntry>}  erfüllt, sobald die Grunddaten geladen sind
	 */
	load(dataPath, options = {}) {
		const sdk = this.sdk;
		if (!sdk) {
			return Promise.reject(new Error("LCC-Web-SDK nicht geladen (window.LCC fehlt). lcc-sdk-three.umd.js nach potree.js einbinden."));
		}

		const viewer = this.viewer;
		const name = options.name || dataPath.split('/').filter(s => s.length > 0).pop();

		let modelMatrix;
		if (options.transform) {
			modelMatrix = options.transform.clone();
		} else {
			const position = options.position instanceof THREE.Vector3
				? options.position
				: new THREE.Vector3().fromArray(options.position || [0, 0, 0]);
			const rotationZ = THREE.MathUtils.degToRad(options.rotationZ || 0);
			const scale = options.scale || 1;

			modelMatrix = new THREE.Matrix4()
				.makeTranslation(position.x, position.y, position.z)
				.multiply(new THREE.Matrix4().makeRotationZ(rotationZ))
				.multiply(new THREE.Matrix4().makeScale(scale, scale, scale))
				.multiply(LCC_TO_POTREE);
		}

		const entry = new LCCEntry(name, dataPath, modelMatrix);
		const camera = viewer.scene.getActiveCamera();

		return new Promise((resolve, reject) => {
			let settled = false;

			const lcc = sdk.load({
				camera: camera,
				scene: viewer.scene.scene,
				renderer: viewer.renderer,
				canvas: viewer.renderer.domElement,
				renderLib: window.THREE,
				dataPath: dataPath,
				modelMatrix: modelMatrix,
				useEnv: options.useEnv !== false,
				useIndexDB: options.useIndexDB !== false,
				useLoadingEffect: options.useLoadingEffect === true,
				appKey: options.appKey || null,
			},
			mesh => {
				entry.mesh = mesh;
				entry.loaded = true;
				this.dispatchEvent({type: 'lcc_loaded', layer: this, entry: entry});
				if (!settled) {
					settled = true;
					resolve(entry);
				}
			},
			progress => {
				this.dispatchEvent({type: 'lcc_progress', layer: this, entry: entry, progress: progress});
				if (options.onProgress) {
					options.onProgress(progress);
				}
			},
			() => {
				this.dispatchEvent({type: 'lcc_failed', layer: this, entry: entry});
				if (!settled) {
					settled = true;
					this.remove(entry);
					reject(new Error(`LCC konnte nicht geladen werden: ${dataPath}`));
				}
			});

			entry.lcc = lcc;
			this.entries.push(entry);
			this.lastCamera = camera;
			sdk.setWriteDepth(this.writeDepth);
			this.guardRendererState();

			this.dispatchEvent({type: 'lcc_added', layer: this, entry: entry});
		});
	}

	/**
	 * Das SDK setzt in load() scene.onAfterRender und lässt den Renderer nach seinem Pass mit
	 * autoClear = true zurück. Potree rendert mehrere Pässe pro Frame mit autoClear = false
	 * (Hintergrund, Punktwolke, Szene, EDL-Komposition, Overlay) – mit autoClear = true löscht
	 * jeder Pass das Bild der vorherigen, übrig bleibt Schwarz. Deshalb nach dem SDK-Hook den
	 * Zustand wiederherstellen.
	 */
	guardRendererState() {
		const scene = this.viewer.scene.scene;
		if (scene.userData.lccGuarded) {
			return;
		}
		const sdkHandler = scene.onAfterRender;
		const renderer = this.viewer.renderer;
		scene.onAfterRender = (...args) => {
			if (typeof sdkHandler === 'function') {
				sdkHandler(...args);
			}
			renderer.autoClear = false;
			this.forceOpaqueAlpha();
		};
		scene.userData.lccGuarded = true;
	}

	/**
	 * Potrees Canvas ist transparent (alpha: true). Der Splat-Pass des SDK hinterlässt in
	 * einzelnen Frames (Sortier-/Index-Updates) Alpha < 1 auf der Splat-Fläche – in MEASUREweb
	 * gemessen: periodisch ein Frame mit 18–28 % der Pixel unter Alpha 250, danach wieder 0. In
	 * diesen Frames scheint die Seite durch den Canvas, sichtbar als Flackern. Deshalb nach dem
	 * Splat-Pass den Alpha-Kanal des Canvas auf 1 setzen (nur Alpha, Farbe bleibt).
	 */
	forceOpaqueAlpha() {
		const renderer = this.viewer.renderer;
		if (renderer.getRenderTarget() !== null) {
			return;		// nur auf dem Canvas; Render-Targets (Screenshot) unangetastet lassen
		}
		const gl = renderer.getContext();
		const clearColor = renderer.getClearColor(new THREE.Color());
		const clearAlpha = renderer.getClearAlpha();

		gl.colorMask(false, false, false, true);
		gl.clearColor(0, 0, 0, 1);
		gl.clear(gl.COLOR_BUFFER_BIT);

		// three-Zustandscache nachziehen: erst auf einen anderen Wert, dann zurück, sonst
		// hält three die Rohänderungen für bereits gesetzt und lässt GL im falschen Zustand.
		renderer.state.buffers.color.setMask(false);
		renderer.state.buffers.color.setMask(true);
		renderer.setClearColor(0x000000, clearAlpha === 1 ? 0 : 1);
		renderer.setClearColor(clearColor, clearAlpha);
	}

	/**
	 * Treffer auf der sichtbaren Splat-Oberfläche: Splats in ein Ziel mit Tiefentextur rendern,
	 * Tiefe am Mauspixel lesen, zurück in Weltkoordinaten. Liefert den Punkt, den der Anwender
	 * sieht. Das Kollisionsnetz des SDK (intersectsRayFromOriginExt) ist eine geglättete Hülle
	 * und lag im Test bis zu 25 cm unter der sichtbaren Fläche – bei flachem Blick wandert der
	 * Netz-Treffer dadurch bis über einen halben Meter zur Seite; von oben stimmen beide auf
	 * den Millimeter. Nur die Splat-Gruppen werden gezeichnet (Messobjekte ausgeblendet).
	 *
	 * @param {{x:number,y:number}} mouse  Mausposition in Canvas-Pixeln (Ursprung oben links)
	 * @param {THREE.Camera} camera
	 * @returns {{position: THREE.Vector3, distance: number} | null}
	 */
	pickVisible(mouse, camera) {
		if (this.entries.length === 0 || !this.entries.some(e => e.loaded && e.visible && e.mesh)) {
			return null;
		}
		const renderer = this.viewer.renderer;
		const size = renderer.getSize(new THREE.Vector2());
		const width = Math.max(1, Math.floor(size.x)), height = Math.max(1, Math.floor(size.y));
		if (mouse.x < 0 || mouse.y < 0 || mouse.x >= width || mouse.y >= height) {
			return null;
		}
		const targets = this.ensurePickTargets(width, height);

		const scene = this.viewer.scene.scene;
		const hidden = [];
		for (const child of scene.children) {
			// Das SDK hängt seine Gruppe in eine eigene Wrapper-Gruppe, deshalb Enthaltensein prüfen
			if (child.visible && !this.entries.some(e => e.mesh && (e.mesh === child || this.contains(child, e.mesh)))) {
				child.visible = false;
				hidden.push(child);
			}
		}

		const prevTarget = renderer.getRenderTarget();
		const clearColor = renderer.getClearColor(new THREE.Color());
		const clearAlpha = renderer.getClearAlpha();
		try {
			renderer.setRenderTarget(targets.depth);
			renderer.setClearColor(0x000000, 0);
			renderer.clear(true, true, true);
			renderer.render(scene, camera);

			renderer.setRenderTarget(targets.read);
			renderer.clear(true, false, false);
			renderer.render(targets.quadScene, targets.quadCamera);
			renderer.readRenderTargetPixels(targets.read, Math.round(mouse.x), height - 1 - Math.round(mouse.y), 1, 1, targets.pixel);
		} finally {
			renderer.setRenderTarget(prevTarget);
			renderer.setClearColor(clearColor, clearAlpha);
			renderer.autoClear = false;		// SDK-Hook setzt true (siehe guardRendererState)
			for (const child of hidden) {
				child.visible = true;
			}
		}

		const depth = targets.pixel[0];
		if (!(depth < 1)) {
			return null;		// kein Splat an dieser Stelle
		}
		const ndc = new THREE.Vector3((mouse.x / width) * 2 - 1, -(mouse.y / height) * 2 + 1, depth * 2 - 1);
		const position = ndc.unproject(camera);
		return {position, distance: camera.position.distanceTo(position)};
	}

	/** true, wenn object unterhalb von root hängt */
	contains(root, object) {
		for (let o = object.parent; o; o = o.parent) {
			if (o === root) {
				return true;
			}
		}
		return false;
	}

	/** Render-Ziele für pickVisible, bei Größenänderung neu angelegt */
	ensurePickTargets(width, height) {
		let t = this.pickTargets;
		if (t && t.width === width && t.height === height) {
			return t;
		}
		if (t) {
			t.depth.dispose();
			t.read.dispose();
		}
		if (!t) {
			// Kopier-Shader: Tiefe aus der Tiefentextur in ein Float-Farbziel, das sich auslesen lässt
			const material = new THREE.ShaderMaterial({
				glslVersion: THREE.GLSL3,
				uniforms: {tDepth: {value: null}},
				depthTest: false,
				depthWrite: false,
				vertexShader: 'out vec2 vUv; void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
				fragmentShader: 'precision highp float; uniform sampler2D tDepth; in vec2 vUv; out vec4 outColor; ' +
					'void main() { outColor = vec4(texture(tDepth, vUv).r, 0.0, 0.0, 1.0); }',
			});
			const quadScene = new THREE.Scene();
			quadScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));
			t = {material, quadScene, quadCamera: new THREE.Camera(), pixel: new Float32Array(4)};
		}
		const depthTexture = new THREE.DepthTexture(width, height, THREE.FloatType);
		t.depth = new THREE.WebGLRenderTarget(width, height, {depthTexture, depthBuffer: true, stencilBuffer: false});
		t.read = new THREE.WebGLRenderTarget(width, height, {type: THREE.FloatType, format: THREE.RGBAFormat, depthBuffer: false, stencilBuffer: false});
		t.material.uniforms.tDepth.value = depthTexture;
		t.width = width;
		t.height = height;
		this.pickTargets = t;
		return t;
	}

	remove(entry) {
		const index = this.entries.indexOf(entry);
		if (index < 0) {
			return;
		}
		this.entries.splice(index, 1);
		if (entry.lcc && this.sdk) {
			this.sdk.unload(entry.lcc);
		}
		this.dispatchEvent({type: 'lcc_removed', layer: this, entry: entry});
	}

	removeAll() {
		for (const entry of [...this.entries]) {
			this.remove(entry);
		}
	}

	/**
	 * Tiefe schreiben: true (Standard) für korrektes Verdecken mit Punktwolke und Messobjekten,
	 * false für weichere Splat-Ränder ohne Verdeckung.
	 */
	setWriteDepth(value) {
		this.writeDepth = value;
		if (this.sdk && this.entries.length > 0) {
			this.sdk.setWriteDepth(value);
		}
	}

	/** Pro Frame vor dem Rendern (viewer.render): Kamera nachführen, SDK-Sortierung/Nachladen */
	update() {
		if (this.entries.length === 0) {
			return;
		}
		const sdk = this.sdk;
		if (!sdk) {
			return;
		}

		const camera = this.viewer.scene.getActiveCamera();
		if (camera !== this.lastCamera) {
			sdk.setCamera(camera);
			this.lastCamera = camera;
		}

		sdk.update();

		// Das SDK rendert in update() je nach Bedarf einen eigenen Pass (Index/Sortierung in ein
		// Render-Target) und lässt danach renderer.autoClear = true zurück. Potree setzt autoClear
		// nur einmal beim Start auf false und verlässt sich für alle Pässe darauf – mit true löscht
		// jeder Pass das Bild des vorherigen (Hintergrund/Punktwolke fehlen einen Frame: Flackern,
		// in HQ fehlt die Punktwolke ganz). Deshalb hier zurücksetzen.
		this.viewer.renderer.autoClear = false;
	}

	/** Umgrenzung aller geladenen Datensätze in Potree-Welt */
	getBoundingBox() {
		const box = new THREE.Box3();
		for (const entry of this.entries) {
			const b = entry.getBoundingBox();
			if (b) {
				box.union(b);
			}
		}
		return box.isEmpty() ? null : box;
	}

	/** Kamera auf einen (oder alle) LCC-Datensätze ausrichten */
	fitToScreen(entry = null) {
		const box = entry ? entry.getBoundingBox() : this.getBoundingBox();
		if (!box) {
			return false;
		}
		const node = new THREE.Object3D();
		node.boundingBox = box;
		this.viewer.zoomTo(node, 1, 500);
		return true;
	}
}
